import GraphemeSplitter from 'grapheme-splitter'
import Riichi from 'riichi'
import { HANDS } from '../constants/hands'

const graphemeSplitter = new GraphemeSplitter()

type GameResult = {
  solution: string
  wind: number
  isTsumo: boolean
  isYakuman: boolean
  solutionIndex: number
  tomorrow: number
}

const tileToAsciiMap: { [id: string]: string } = {
  '🀇': '1m',
  '🀈': '2m',
  '🀉': '3m',
  '🀊': '4m',
  '🀋': '5m',
  '🀌': '6m',
  '🀍': '7m',
  '🀎': '8m',
  '🀏': '9m',
  '🀙': '1p',
  '🀚': '2p',
  '🀛': '3p',
  '🀜': '4p',
  '🀝': '5p',
  '🀞': '6p',
  '🀟': '7p',
  '🀠': '8p',
  '🀡': '9p',
  '🀐': '1s',
  '🀑': '2s',
  '🀒': '3s',
  '🀓': '4s',
  '🀔': '5s',
  '🀕': '6s',
  '🀖': '7s',
  '🀗': '8s',
  '🀘': '9s',
  '🀀': '1z',
  '🀁': '2z',
  '🀂': '3z',
  '🀃': '4z',
  '🀆': '5z',
  '🀅': '6z',
  '🀄': '7z',
}

const tileToUnicodeMap: { [id: string]: string } = {
  '1m': '🀇',
  '2m': '🀈',
  '3m': '🀉',
  '4m': '🀊',
  '5m': '🀋',
  '6m': '🀌',
  '7m': '🀍',
  '8m': '🀎',
  '9m': '🀏',
  '1p': '🀙',
  '2p': '🀚',
  '3p': '🀛',
  '4p': '🀜',
  '5p': '🀝',
  '6p': '🀞',
  '7p': '🀟',
  '8p': '🀠',
  '9p': '🀡',
  '1s': '🀐',
  '2s': '🀑',
  '3s': '🀒',
  '4s': '🀓',
  '5s': '🀔',
  '6s': '🀕',
  '7s': '🀖',
  '8s': '🀗',
  '9s': '🀘',
  '1z': '🀀',
  '2z': '🀁',
  '3z': '🀂',
  '4z': '🀃',
  '5z': '🀆',
  '6z': '🀅',
  '7z': '🀄',
}

export const convertHandToAscii = (hand: string) => {
  var tiles = graphemeSplitter.splitGraphemes(hand)
  var lastTile = tiles.pop()
  if (lastTile !== undefined) {
    return (
      tiles.map((tile: string) => tileToAsciiMap[tile]).join('') +
      `${isTsumo ? '' : '+'}${tileToAsciiMap[lastTile]}+${wind}`
    )
  }
  return ''
}

export const convertHandToUnicode = (hand: string) => {
  const tiles = hand.slice(0, 26).match(/.{1,2}/g)
  const lastTile: string =
    hand[26] !== '+' ? hand.slice(26, 28) : hand.slice(27, 29)
  if (tiles !== null) {
    return (
      tiles.map((tile: string) => tileToUnicodeMap[tile]).join('') +
      tileToUnicodeMap[lastTile]
    )
  }
  return ''
}

export const isInvalidHand = (word: string) => {
  const tiles = graphemeSplitter.splitGraphemes(word)
  const counts: { [id: string]: number } = {}

  for (const tile of tiles) {
    counts[tile] = counts[tile] ? counts[tile] + 1 : 1
  }

  for (const tile in counts) {
    if (Object.prototype.hasOwnProperty.call(counts, tile)) {
      if (counts[tile] > 4) {
        return false
      }
    }
  }
  return true
}

export const isWordInWordList = (word: string) => {
  const riichiCalc = new Riichi(convertHandToAscii(word)).calc()
  return riichiCalc.isAgari && Object.keys(riichiCalc.yaku).length > 0
}

export const isWinningWord = (word: string) => {
  return solution === word
}

export const getWordOfDay = () => {
  const index = Math.floor(Math.random() * HANDS.length)
  const hand = HANDS[index]

  return {
    solution: convertHandToUnicode(hand),
    wind: parseInt(hand.slice(-2)),
    isTsumo: hand[26] !== '+',
    isYakuman: false,
    solutionIndex: index,
    tomorrow: 0,
  }
}

const YAKUMAN_HANDS = [
  // 国士无双 (和牌张覆盖全部13种幺九牌)
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+1m+11',
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+9m+12',
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+1p+13',
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+9p+14',
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+1s+21',
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+9s+22',
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+1z+23',
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+2z+24',
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+3z+31',
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+4z+32',
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+5z+33',
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+6z+34',
  '1m1p1s1z2z3z4z5z6z7z9m9p9s+7z+41',
  // 四暗刻 (和牌张多样)
  '1m1m1m3p3p3p7s7s7s2z2z2z5z+5z+42',
  '2m2m2m6p6p6p9s9s9s1z1z1z4z+4z+43',
  '5m5m5m8p8p8p3s3s3s6z6z6z7z+7z+44',
  '1p1p1p4p4p4p9p9p9p3z3z3z7z+7z+11',
  '2p2p2p5s5s5s8s8s8s1z1z1z6z+6z+12',
  '3m3m3m7p7p7p2s2s2s4z4z4z5z+5z+13',
  '4m4m4m6m6m6m8p8p8p2z2z2z7z+7z+14',
  '9m9m9m1p1p1p6s6s6s3z3z3z5z+5z+21',
  '1s1s1s4s4s4s7s7s7s1z1z1z2z+2z+22',
  '2m2m2m5m5m5m8m8m8m6z6z6z7z+7z+23',
  '1m1m1m3p3p3p7s7s7s2z2z2z5z5z+34',
  '2m2m2m6p6p6p9s9s9s1z1z1z4z4z+41',
  '5m5m5m8p8p8p3s3s3s6z6z6z7z7z+42',
  // 九莲宝灯 (万子和1-9, 筒子和1-9, 索子和1-9)
  '1m1m1m2m3m4m5m6m7m8m9m9m9m+1m+43',
  '1m1m1m2m3m4m5m6m7m8m9m9m9m+2m+44',
  '1m1m1m2m3m4m5m6m7m8m9m9m9m+3m+11',
  '1m1m1m2m3m4m5m6m7m8m9m9m9m+4m+12',
  '1m1m1m2m3m4m5m6m7m8m9m9m9m+5m+13',
  '1m1m1m2m3m4m5m6m7m8m9m9m9m+6m+14',
  '1m1m1m2m3m4m5m6m7m8m9m9m9m+7m+21',
  '1m1m1m2m3m4m5m6m7m8m9m9m9m+8m+22',
  '1m1m1m2m3m4m5m6m7m8m9m9m9m+9m+23',
  '1p1p1p2p3p4p5p6p7p8p9p9p9p+1p+24',
  '1p1p1p2p3p4p5p6p7p8p9p9p9p+3p+31',
  '1p1p1p2p3p4p5p6p7p8p9p9p9p+5p+32',
  '1p1p1p2p3p4p5p6p7p8p9p9p9p+7p+33',
  '1p1p1p2p3p4p5p6p7p8p9p9p9p+9p+34',
  '1s1s1s2s3s4s5s6s7s8s9s9s9s+2s+41',
  '1s1s1s2s3s4s5s6s7s8s9s9s9s+4s+42',
  '1s1s1s2s3s4s5s6s7s8s9s9s9s+6s+43',
  '1s1s1s2s3s4s5s6s7s8s9s9s9s+8s+44',
  // 大三元 (和牌张覆盖5z/6z/7z)
  '2m3m4m1s1s5z5z6z6z6z7z7z7z+5z+11',
  '3p4p5p8s8s5z5z6z6z6z7z7z7z+5z+12',
  '6m7m8m3p3p5z5z6z6z6z7z7z7z+5z+13',
  '2m3m4m1s1s5z5z5z6z6z7z7z7z+6z+14',
  '3p4p5p8s8s5z5z5z6z6z7z7z7z+6z+21',
  '6m7m8m3p3p5z5z5z6z6z7z7z7z+6z+22',
  '2m3m4m1s1s5z5z5z6z6z6z7z7z+7z+23',
  '3p4p5p8s8s5z5z5z6z6z6z7z7z+7z+24',
  '6m7m8m3p3p5z5z5z6z6z6z7z7z+7z+31',
  // 字一色 (和牌张覆盖1z-7z)
  '1z1z1z2z2z2z3z3z3z5z5z5z6z+6z+32',
  '1z1z1z2z2z2z3z3z3z6z6z6z7z+7z+33',
  '2z2z2z3z3z3z4z4z4z6z6z6z1z+1z+34',
  '1z1z1z3z3z3z4z4z4z7z7z7z5z+5z+41',
  '2z2z2z4z4z4z5z5z5z7z7z7z6z+6z+42',
  '1z1z1z2z2z2z5z5z5z7z7z7z4z+4z+43',
  '3z3z3z4z4z4z5z5z5z6z6z6z2z+2z+44',
  '1z1z1z2z2z2z4z4z4z5z5z5z3z+3z+11',
  // 绿一色 (和牌张覆盖2s/3s/4s/6s/8s/6z)
  '2s2s2s3s3s3s4s4s4s6s6s8s8s+8s+12',
  '2s2s2s3s3s3s4s4s4s8s8s8s6s+6s+13',
  '2s2s2s3s3s3s6s6s6s8s8s8s4s+4s+14',
  '2s2s3s3s4s4s6s6s6s8s8s8s2s+2s+21',
  '2s2s2s3s3s3s4s4s4s6s6s6z6z+6z+22',
  '3s3s3s4s4s4s6s6s6s8s8s8s2s+2s+23',
  '2s2s3s3s4s4s6s6s6s6z6z6z8s+8s+24',
  // 清老头 (和牌张覆盖1m/9m/1p/9p/1s/9s)
  '1m1m1m9m9m9m1p1p1p9p9p9p1s+1s+31',
  '1m1m1m9m9m9m1p1p1p1s1s1s9s+9s+32',
  '1m1m1m9m9m9m9p9p9p9s9s9s1p+1p+33',
  '1m1m1m1p1p1p9p9p9p9s9s9s9m+9m+34',
  '9m9m9m1p1p1p9p9p9p1s1s1s1m+1m+41',
  '1m1m1m9m9m9m1s1s1s9s9s9s9p+9p+42',
  // 小四喜 (和牌张覆盖1z/2z/3z/4z)
  '5m5m5m1z1z1z2z2z2z3z3z3z4z+4z+43',
  '8p8p8p1z1z1z2z2z2z3z3z4z4z+3z+44',
  '9s9s9s1z1z1z2z2z3z3z3z4z4z+2z+11',
  '6m6m6m1z1z2z2z2z3z3z3z4z4z+1z+12',
  '5m5m5m1z1z1z2z2z2z3z3z4z4z+4z+13',
  '8p8p1z1z1z2z2z2z3z3z3z4z4z+4z+14',
  '9s9s1z1z1z2z2z2z3z3z3z4z4z+4z+21',
  // 大四喜 (和牌张覆盖1z/2z/3z/4z)
  '5m5m1z1z1z2z2z2z3z3z3z4z4z+4z+22',
  '8p8p1z1z1z2z2z2z3z3z4z4z4z+3z+23',
  '9s9s1z1z1z2z2z3z3z3z4z4z4z+2z+24',
  '6m6m1z1z2z2z2z3z3z3z4z4z4z+1z+31',
  '3p3p1z1z1z2z2z2z3z3z3z4z4z+4z+32',
  '7s7s1z1z1z2z2z2z3z3z3z4z4z+4z+33',
  // 大七星 (字一色七对，自摸)
  '1z1z2z2z3z3z4z4z5z5z6z6z7z7z+11',
  '1z1z2z2z3z3z4z4z5z5z6z6z7z7z+12',
  '1z1z2z2z3z3z4z4z5z5z6z6z7z7z+13',
]

const getYakumanHand = () => {
  const index = Math.floor(Math.random() * YAKUMAN_HANDS.length)
  const hand = YAKUMAN_HANDS[index]
  return {
    solution: convertHandToUnicode(hand),
    wind: parseInt(hand.slice(-2)),
    isTsumo: hand[26] !== '+',
    isYakuman: true,
    solutionIndex: -1,
    tomorrow: 0,
  }
}

let gameCount = 0
const YAKUMAN_INTERVAL = 20

export let { solution, wind, isTsumo, solutionIndex, tomorrow } =
  getWordOfDay()

const setCurrentGame = (result: GameResult) => {
  solution = result.solution
  wind = result.wind
  isTsumo = result.isTsumo
  solutionIndex = result.solutionIndex
  tomorrow = result.tomorrow
  return result
}

export const loadGameForServerCount = (serverGamesPlayed: number) => {
  const result =
    serverGamesPlayed > 0 && serverGamesPlayed % YAKUMAN_INTERVAL === 0
      ? getYakumanHand()
      : getWordOfDay()
  return setCurrentGame(result)
}

export const newGame = (currentGameCount?: number) => {
  gameCount++
  const yakumanCounter = currentGameCount ?? gameCount
  const result = yakumanCounter % YAKUMAN_INTERVAL === 0
    ? getYakumanHand()
    : getWordOfDay()
  return setCurrentGame(result)
}
